import { Button } from '@/components/common/Button';
import { RiskyDisclosureParagraph } from '@/components/common/DisclosureParagraph';
import {
    EntityViewContent,
    entityViewInset,
} from '@/components/common/EntityView';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { AnnotationQuestion, TwoPeople } from '@/components/common/icons';
import Icon from '@/components/common/icons/Icon';
import InfoText from '@/components/common/InfoText';
import RadioInputGroup from '@/components/common/RadioInputGroup';
import { useToasts } from '@/components/common/Toasts';
import {
    EyebrowHeader,
    Header,
    LinkText,
    Paragraph,
    Text,
} from '@/components/common/Typography';
import OpenAccountCallout from '@/components/goals/OpenAccountCallout';
import { useIdvPosition } from '@/components/idv/useIdvPosition';
import { showInviteModal } from '@/components/partnershipInvites/InviteModal';
import { useMutation } from '@apollo/client';
import { Divider, FlexProps, Spacer } from '@chakra-ui/react';
import { HELP_CENTER_LINK } from '@common/compliance';
import { assert } from '@common/errors';
import { Goal } from '@common/goals';
import {
    GoalAccountStructure,
    GoalType,
    UpdateGoalDocument,
} from '@common/graphql/generated';
import {
    iconByGoalAccountInvestmentType,
    titleByGoalAccountInvestmentType,
    useAccountStructureChoices,
    useCreateGoal,
} from '@common/react/goals';
import { ACCOUNT_STRUCTURE_OPTIONS } from '@common/react/goals/goalAccountStructure';
import { usePageContext } from '@common/react/pageMode';
import { isSingleplayer, useCurrentUser } from '@common/react/user';
import { TOASTS } from '@common/toasts';
import React, { useState } from 'react';
import { configByGoalType } from '../../config';
import {
    CashPlusDescription,
    CashPlusLabel,
    ClassicPortfolioDescription,
    ClassicPortfolioLabel,
    PremiumPortfolioDescription,
    PremiumPortfolioLabel,
} from '../InvestmentTypes';
import GoalOnboardingAccountTypeQuestionAndAnswer from './GoalOnboardingAccountTypeQuestionAndAnswer';
import { UpdateGoalOnboardingFcn } from './GoalOnboardingContext';
import GoalOnboardingIntroHeader from './GoalOnboardingIntroHeader';

interface GoalOnboardingAccountType extends FlexProps {
    goalType: GoalType;
    initialAccountStructure?: GoalAccountStructure;
    goal?: Goal;
    updateGoalOnboarding?: UpdateGoalOnboardingFcn;
    isUpdating?: boolean;
    onComplete: (goalId: string) => void;
}
const GoalOnboardingAccountType = ({
    goalType,
    initialAccountStructure,
    goal,
    updateGoalOnboarding,
    isUpdating,
    onComplete,
    ...props
}: GoalOnboardingAccountType) => {
    const currentUser = useCurrentUser();
    const singleplayer = isSingleplayer(currentUser);
    const { pageMode, setPageMode } = usePageContext();
    const { activePartnershipId } = useCurrentUser();
    const [updateGoal] = useMutation(UpdateGoalDocument);
    const [accountStructure, setAccountStructure] = useState(
        singleplayer ? 'PRIVATE' : initialAccountStructure
    );
    const createGoal = useCreateGoal();
    const { showErrorToast } = useToasts();
    const { canChooseJointAccount, canChooseSplitAccount } =
        useAccountStructureChoices();
    const idvPosition = useIdvPosition();
    const isIdvComplete = idvPosition === 'complete';

    const finishStep = async () => {
        if (!isIdvComplete) return;

        if (goal) {
            assert(updateGoalOnboarding, 'Missing updateGoalOnboarding');
            await updateGoal({
                variables: {
                    id: goal.id,
                    accountStructure,
                },
            });
            await updateGoalOnboarding({
                params: {
                    info: {
                        lastCompletedStep: 'ACCOUNT_TYPE',
                    },
                },
            });
            onComplete(goal.id);
        } else {
            assert(accountStructure, 'Missing accountType');
            await createGoal({
                params: {
                    name: configByGoalType[goalType].defaultName,
                    type: goalType,
                    typeInfo: {},
                    accountStructure,
                    onboardingVersion: 'v1',
                },
                onComplete: (g) => {
                    onComplete(g.id);
                },
                onError: () => {
                    showErrorToast(TOASTS.unexpectedErrorPleaseReachOut);
                },
            });
        }

        // If account type can only be shown in a page mode different from the
        // current one, switch to that page mode.
        if (accountStructure === 'PRIVATE' && pageMode !== 'MINE') {
            setPageMode('MINE');
        } else if (accountStructure === 'JOINT' && pageMode !== 'PUBLIC') {
            setPageMode('PUBLIC');
        }
    };

    return (
        <EntityViewContent pt={0} {...props}>
            <FadeInBox>
                <GoalOnboardingIntroHeader goal={goal} goalType={goalType} />
                <FlexCol
                    px={entityViewInset}
                    pt={12}
                    pb={12}
                    bg={isIdvComplete ? 'white' : 'gray.50'}
                >
                    {isIdvComplete ? (
                        <Header mb={8}>
                            {singleplayer
                                ? 'Click ‘Next‘ to begin opening this investment goal'
                                : 'Start by choosing your type of investment goal'}
                        </Header>
                    ) : (
                        <OpenAccountCallout
                            title="Open a Plenty investment account to unlock this goal"
                            mb={12}
                            bg="white"
                            shadow="sm"
                        />
                    )}

                    {!singleplayer && (
                        <RadioInputGroup
                            name="accountType"
                            value={accountStructure ?? ''}
                            // TODO: Get types flowing through this component properly.
                            onChange={(v) =>
                                setAccountStructure(v as GoalAccountStructure)
                            }
                            direction="row"
                            gap={3}
                            options={ACCOUNT_STRUCTURE_OPTIONS.map((o) => {
                                return {
                                    value: o.value,
                                    isDisabled:
                                        !isIdvComplete ||
                                        {
                                            JOINT: !canChooseJointAccount,
                                            SPLIT: !canChooseSplitAccount,
                                            PRIVATE: false,
                                        }[o.value],
                                    header: (
                                        <>
                                            <Icon
                                                name={o.iconName}
                                                color="blue.400"
                                                size={12}
                                                mr={2}
                                            />
                                            <Text fontWeight="medium">
                                                {o.title}
                                            </Text>
                                        </>
                                    ),
                                    label: (
                                        <>
                                            <Text
                                                fontSize="sm"
                                                color="gray.600"
                                                mt={1}
                                                mb={6}
                                                data-cy={`account-type-${o.value}`}
                                            >
                                                {o.description({
                                                    activePartnershipId,
                                                })}
                                            </Text>
                                            <Text
                                                fontSize="xs"
                                                fontWeight="medium"
                                                color="textMuted"
                                                mb={2}
                                            >
                                                Investment types
                                            </Text>
                                            <FlexCol
                                                gap={2}
                                                alignItems="flex-start"
                                            >
                                                {o.investmentTypes.map(
                                                    (investmentType) => {
                                                        const iconName =
                                                            iconByGoalAccountInvestmentType[
                                                                investmentType
                                                            ];
                                                        const title =
                                                            titleByGoalAccountInvestmentType[
                                                                investmentType
                                                            ];
                                                        return (
                                                            <FlexRow
                                                                key={
                                                                    investmentType
                                                                }
                                                                alignItems="center"
                                                                borderColor="border"
                                                                borderRadius="12px 3px 12px 3px"
                                                                borderWidth={1}
                                                                px={2}
                                                                py={1}
                                                            >
                                                                <Icon
                                                                    name={
                                                                        iconName
                                                                    }
                                                                    size="xs"
                                                                    p="1px" // xs isn't quite small enough
                                                                    mr={1}
                                                                />
                                                                <Text
                                                                    fontSize="xs"
                                                                    fontWeight="medium"
                                                                    color="textMuted"
                                                                >
                                                                    {title}
                                                                </Text>
                                                            </FlexRow>
                                                        );
                                                    }
                                                )}
                                            </FlexCol>
                                        </>
                                    ),
                                };
                            })}
                        />
                    )}
                    <FlexRow
                        justifyContent="space-between"
                        alignItems="center"
                        pt={8}
                        pl={4}
                    >
                        {isIdvComplete ? (
                            activePartnershipId ? (
                                <InfoText iconName="LightBulb04">
                                    We’ll save your progress if you don’t finish
                                    setting up your goal
                                </InfoText>
                            ) : (
                                <FlexRow gap={2}>
                                    <LinkText
                                        onClick={() => {
                                            showInviteModal();
                                        }}
                                    >
                                        <TwoPeople size="lg" mr={2} />
                                        Invite your partner to unlock new
                                        investments
                                    </LinkText>
                                </FlexRow>
                            )
                        ) : (
                            <Spacer />
                        )}

                        <Button
                            onClick={finishStep}
                            onClickEvent="Goal Onboarding Continue Button Clicked"
                            onClickEventParams={{
                                step: 'ACCOUNT_TYPE',
                                account_structure: accountStructure,
                            }}
                            isLoading={isUpdating}
                            disabled={
                                !isIdvComplete ||
                                !accountStructure ||
                                isUpdating
                            }
                        >
                            Next
                        </Button>
                    </FlexRow>
                </FlexCol>
                <FlexRow px={entityViewInset} pt={12}>
                    <FlexRow flex={1}>
                        <InvestmentTypeGrid />
                    </FlexRow>
                    <FlexRow flex={1}>
                        <GoalOnboardingAccountTypeQuestionAndAnswer />
                    </FlexRow>
                </FlexRow>

                <FlexRow my={8} alignItems="center" px={entityViewInset}>
                    <AnnotationQuestion color="gray.500" mr={2} />
                    <Paragraph color="gray.500">
                        Have a question? Check out our{' '}
                        <LinkText href={HELP_CENTER_LINK} target="_blank">
                            help center
                        </LinkText>{' '}
                        or email us at{' '}
                        <LinkText href="mailto:members@withplenty.com">
                            members@withplenty.com
                        </LinkText>
                    </Paragraph>
                </FlexRow>

                <FlexRow px={entityViewInset} pb={12}>
                    <RiskyDisclosureParagraph />
                </FlexRow>
            </FadeInBox>
        </EntityViewContent>
    );
};

const InvestmentTypeGrid = () => (
    <FlexCol mr={10}>
        <EyebrowHeader pb={8}>Types of investments</EyebrowHeader>
        <FlexCol>
            <CashPlusLabel mb={4} />
            <CashPlusDescription displayArrowLinkInline={true} />
        </FlexCol>
        <Divider my={8} />
        <FlexCol>
            <PremiumPortfolioLabel mb={4} />
            <PremiumPortfolioDescription displayArrowLinkInline={true} />
        </FlexCol>
        <Divider my={8} />
        <FlexCol>
            <ClassicPortfolioLabel mb={4} />
            <ClassicPortfolioDescription displayArrowLinkInline={true} />
        </FlexCol>
    </FlexCol>
);

export default GoalOnboardingAccountType;
